import { IDeliverySelectorData } from "../../api/estore/checkout/interfaces/delivery-selector-data-interface.js";
import { DeliverySelectorOutputStateManager } from "./delivery-selector-output-state.manager.js";

export class DeliverySelectorStateManager {
    state: IDeliverySelectorData;
    outputStateManager: DeliverySelectorOutputStateManager;

    stateReplaceDelegates: { (oldState: IDeliverySelectorData, newState: IDeliverySelectorData) : void }[] = [];
    selectedGroupDelegates: { (oldGroupId: string, newGroupId: string) : void }[] = [];
    selectedOptionDelegates: { (oldOptionId: string, newOptionId: string) : void }[] = [];
    checkedProviderCheckboxDelegates: { (optionId: string, oldState: boolean, newState: boolean) : void }[] = [];
    pickupPointChangedDelegates: { (optionId: string, oldPickupPointId: string, newPickupPointId: string) : void }[] = [];
    onProviderCheckboxInfoVisibleChangeDelegates: { (optionId: string, oldState: boolean, newState: boolean) : void }[] = [];

    constructor(initialState: IDeliverySelectorData) {

        this.outputStateManager = new DeliverySelectorOutputStateManager(initialState);

        this.state = initialState;
    }

    // State change function
    replaceState(newState: IDeliverySelectorData) {
        const oldState = this.state;
        this.outputStateManager.updateStateFromOutputState(newState);
        this.state = newState;
        this.stateReplaceDelegates.forEach(d => d(oldState, newState));
    }

    setSelectedGroup(groupId: string) {
        const selectedGroups = this.state.groups.filter(g => g.selected);
        const currentSelected = selectedGroups[0]?.id || null;
        selectedGroups.forEach(g => g.selected = false);
        this.state.groups.forEach(g => g.selected = g.id === groupId);
        this.selectedGroupDelegates.forEach(d => d(currentSelected, groupId));
    }

    setSelectedOption(optionId: string) {
        const allOptions = this.state.groups.map(g => g.options).flat();
        const selectedOptions = allOptions.filter(o => o.selected);
        const currentSelected = selectedOptions[0]?.id || null;

        allOptions.forEach(o => o.selected = o.id === optionId);
        this.selectedOptionDelegates.forEach(d => d(currentSelected, optionId));

        const selectedOption = allOptions.find(o => o.id === optionId);
        this.outputStateManager.setOptionSelected(optionId, selectedOption.providerCheckboxChecked);
    }

    setProviderCheckboxState(optionId: string, value: boolean) {
        const allOptions = this.state.groups.map(g => g.options).flat();
        const option = allOptions.filter(o => o.id === optionId)[0] || null;
        if (!option) {
            throw Error(`Delivery option with ID ${optionId} was not found!`);
        }

        const oldValue = option.providerCheckboxChecked;
        option.providerCheckboxChecked = value;
        
        this.checkedProviderCheckboxDelegates.forEach(d => d(optionId, oldValue, value));
        
        this.outputStateManager.setOptionChecked(optionId, value);
    }

    setSelectedPickupPoint(optionId:string, pickupPointId: string) {
        const allOptions = this.state.groups.map(g => g.options).flat();
        const option = allOptions.filter(o => o.id === optionId)[0] || null;
        if (!option) {
            throw Error(`Delivery option with ID ${optionId} was not found!`);
        }

        const oldSelectedPickupPointId = option.providerPickupPoints.filter(p => p.selected)[0]?.id || null;
        option.providerPickupPoints.forEach(p => p.selected = p.id === pickupPointId);

        this.pickupPointChangedDelegates.forEach(d => d(optionId, oldSelectedPickupPointId, pickupPointId));
        
        this.outputStateManager.setOptionPickupPointId(optionId, pickupPointId);
    }

    toggleProviderCheckboxInfoVisible(optionId: string) {
        const allOptions = this.state.groups.map(g => g.options).flat();
        const option = allOptions.filter(o => o.id === optionId)[0] || null;
        if (!option) {
            throw Error(`Delivery option with ID ${optionId} was not found!`);
        }

        const currentState = option.providerCheckboxTooltipVisible;
        option.providerCheckboxTooltipVisible = !currentState;

        this.onProviderCheckboxInfoVisibleChangeDelegates.forEach(d => d(optionId, currentState, !currentState));
    }

    setProviderInputValue(optionId: string, value: string) {
        this.outputStateManager.setOptionInputValue(optionId, value);
    }
    // #State change function
}